/**
 * Check if a string is empty or not.
 * @param  {String}  value Value to check
 * @return {Boolean}       True if value had character, false otherwise
 */
function isNonEmpty(value) {
    return typeof value === 'string' && value.trim() !== '';
}

/**
 * Return an URL with dimension in the path (image.jpg => image.500x500.jpg)
 * @param {String} url
 * @param {String} width
 * @param {String} height
 * @throw {Error} If one parameter at least is missing
 */
function urlWithDimensions(url, width, height) {
    if (!url || !width || !height) {
        throw new Error('`url`, `width` and `height` parameters are all required');
    }
    if (url.indexOf('.o.') > -1) {
        return url.replace('.o.', `.${width}x${height}.`);
    }
    return url.replace(/(.*)\.([a-z0-9]+)$/, `$1.${width}x${height}.$2`);
}

/**
 *
 * @param {String} coordinatesString
 * @return {{lat: *, lng: *}}
 */
function stringToCoordinates(coordinatesString) {
    if (!coordinatesString) {
        return null;
    }
    const parts = coordinatesString.split(/,\s?/);
    const coordinates = {
        lng: Number(parts[0]),
        lat: Number(parts[1]),
    };
    if (isNaN(coordinates.lat) || isNaN(coordinates.lng)) {
        throw new Error('You must provide coordinates with string in format `0.0000,0.0000`');
    }
    return coordinates;
}

/**
 *
 * @param {Object} coordinates
 * @return {string}
 */
function coordinatesToString(coordinates) {
    return `${coordinates.lng},${coordinates.lat}`;
}

/**
 * Compute the edit distance between the two given strings
 * @param {String} a
 * @param {String} b
 * @return {Number}
 */
function getEditDistance(a, b) {
    if (a.length === 0) return b.length;
    if (b.length === 0) return a.length;

    const matrix = [];

    // increment along the first column of each row
    let i;
    for (i = 0; i <= b.length; i++) {
        matrix[i] = [i];
    }

    // increment each column in the first row
    let j;
    for (j = 0; j <= a.length; j += 1) {
        matrix[0][j] = j;
    }

    // Fill in the rest of the matrix
    for (i = 1; i <= b.length; i++) {
        for (j = 1; j <= a.length; j += 1) {
            if (b.charAt(i - 1) === a.charAt(j - 1)) {
                matrix[i][j] = matrix[i - 1][j - 1];
            } else {
                matrix[i][j] = Math.min(
                    matrix[i - 1][j - 1] + 1, // substitution
                    Math.min(
                        matrix[i][j - 1] + 1, // insertion
                        matrix[i - 1][j] + 1,
                    ),
                ); // deletion
            }
        }
    }

    return matrix[b.length][a.length];
}

/**
 * returns the hash of the provided string with the provided algorithm
 * @param value value
 * @param algorithm algorithm in `SHA-***` format
 * @returns {Promise<string>}
 */
const getHash = (value, algorithm) => {
    const utf8 = new TextEncoder().encode(value);
    if (crypto) {
        return crypto.subtle.digest(algorithm, utf8).then((hashBuffer) => {
            const hashArray = Array.from(new Uint8Array(hashBuffer));
            return hashArray.map((bytes) => bytes.toString(16).padStart(2, '0')).join('');
        });
    }
    return Promise.resolve('');
};

export {
    isNonEmpty,
    urlWithDimensions,
    stringToCoordinates,
    coordinatesToString,
    getEditDistance,
    getHash,
};
